import {Component, ElementRef, forwardRef, Input, OnInit, ViewChild} from '@angular/core';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import {
  MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {PerformerService} from '../../../core/services/performer/performer.service';
import {SpecialtyModel} from '../../../core/models/provider.model';
import {startWith} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs';
import {MatChipInputEvent} from '@angular/material/chips';
import {SnackbarService} from '../../../core/services/snackbar.service';

@Component({
  selector: 'app-specialties-chip',
  templateUrl: './specialties-chip.component.html',
  styleUrls: ['./specialties-chip.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SpecialtiesChipComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SpecialtiesChipComponent),
      multi: true,
    },
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: {autoActiveFirstOption: true},
    },
  ],
})
export class SpecialtiesChipComponent implements OnInit, ControlValueAccessor {
  @Input() set data(value) {
    this.allSpecialties = value;
    this.filteredSpecialties.next(this._filterList());
  }
  _selected: SpecialtyModel[] = [];
  @Input() set selected(specialties: SpecialtyModel[]) {
    this.writeValue(specialties);
  }

  allSpecialties: SpecialtyModel[]; // all items without changes
  filteredSpecialties = new BehaviorSubject<SpecialtyModel[]>([]); // items without specialties
  specialtiesFormArray = new FormArray([]);
  specialtyCtrl = new FormControl();

  public selectable = true;
  public removable = true;
  public addOnBlur = false;
  public separatorKeysCodes: number[] = [ENTER, COMMA];

  @ViewChild('specialtyInput', {read: ElementRef}) public specialtyInput: ElementRef;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  constructor(
    private performerService: PerformerService,
    private fb: FormBuilder,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit() {
    this.specialtyCtrl.valueChanges.pipe(startWith(null)).subscribe((value: string | null) => {
      this.filteredSpecialties.next(typeof value === 'string' ? this._filter(value) : this._filterList());
    });
  }

  _filterList(): SpecialtyModel[] {
    const selectedIds = this._selected.map((s) => +s.id);

    return this.allSpecialties.filter((r) => !selectedIds.includes(+r.id));
  }

  private _filter(value: string): SpecialtyModel[] {
    const filterValue = value.toLowerCase();

    return this._filterList().filter((specialty) => specialty.name.toLowerCase().indexOf(filterValue) === 0);
  }

  public removeSpecialty(specialty): void {
    const index = this._selected.findIndex((s) => s.id === specialty.id);

    if (index >= 0) {
      this.removeSelectedSpecialty(index);
    }
  }

  removeSelectedSpecialty(index: number) {
    this._selected.splice(index, 1);
    this.specialtiesFormArray.removeAt(index);
    this.filteredSpecialties.next(this._filterList());
  }

  specialtySelected(specialty: SpecialtyModel) {
    this._selected.push(specialty);
    this.addSpecialtyCtrl(specialty);
    this.filteredSpecialties.next(this._filterList());
  }

  addSpecialtyCtrl(specialty: SpecialtyModel) {
    this.specialtiesFormArray.push(this.createSpecialtyGroup(specialty));
  }

  public selectSpecialty(event: MatAutocompleteSelectedEvent): void {
    const specialty = event.option.value;

    this.specialtySelected(specialty);
    this.specialtyInput.nativeElement.value = '';
    this.specialtyCtrl.setValue(null);
    this.specialtyCtrl.disable();
    this.specialtyCtrl.enable();
  }

  createSpecialtyGroup(specialty: SpecialtyModel): FormGroup {
    const group = this.fb.group({
      description: [''],
      name: [''],
      id: [null],
      organizationId: [null],
      isDeleted: [false],
      private: [false],
    });

    if (specialty) group.patchValue(specialty);

    return group;
  }

  createSpecialtiesFormArray(specialties: SpecialtyModel[]): FormArray {
    return this.fb.array(specialties.map((s) => this.createSpecialtyGroup(s)));
  }

  checkIfExist(specialty: SpecialtyModel) {
    return this._selected.find(({name}) => name === specialty.name);
  }

  add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      const newSpecialty = {name: value} as SpecialtyModel;

      if (!this.checkIfExist(newSpecialty)) {
        this.specialtySelected(newSpecialty);

        // Reset the input value
        if (input) {
          input.value = '';
        }

        this.specialtyCtrl.setValue(null);
      } else {
        this.snackbarService.notify(`You can't add the same specialty`);
      }
    }
  }

  public onTouched: () => void = () => {};

  writeValue(specialties: SpecialtyModel[] = []): void {
    this._selected = specialties;
    if (specialties.length) {
      this.specialtiesFormArray = this.createSpecialtiesFormArray(specialties);
      this.filteredSpecialties.next(this._filterList());
    }
  }

  registerOnChange(fn: any): void {
    this.specialtiesFormArray.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.specialtiesFormArray.disable() : this.specialtiesFormArray.enable();
  }

  validate(): ValidationErrors | null {
    return this.specialtiesFormArray.valid
      ? null
      : {invalidForm: {valid: false, message: 'specialtiesArray fields are invalid'}};
  }
}
